import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import Routes from '../utils/routes.js.erb';
import { authHeaders } from '../actions';

export const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

const fetchConversation = async (id) => {
  const response = await fetch(Routes.api_dashboard_communication_conversation_path(id), {
    headers: authHeaders(),
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error('Error fetching conversation');
  }

  return response.json();
};

export const useConversation = (id) => {
  const queryClient = useQueryClient();

  const result = useQuery({
    queryKey: ['conversation', id],
    queryFn: () => fetchConversation(id),
    keepPreviousData: true,
    retry: false,
    enabled: typeof id !== 'undefined' && id !== null,
  });

  const invalidateConversation = () => {
    queryClient.invalidateQueries({ queryKey: ['conversation', id] });
  }

  return {
    ...result,
    invalidateConversation,
  }
}

const buildFilterParams = ({ pageParam, query, groupIds, subgroupIds, cohortIds, pinnedPlayerId }) => {
  const params = { page: pageParam, q: query };
  if (groupIds?.length) params.group_ids = groupIds;
  if (subgroupIds?.length) params.subgroup_ids = subgroupIds;
  if (cohortIds?.length) params.cohort_ids = cohortIds;
  if (pinnedPlayerId) params['pinned_player_id'] = pinnedPlayerId;
  return params;
};

const fetchConversations = async ({ pageParam, query, groupIds, subgroupIds, cohortIds, pinnedPlayerId }) => {
  const response = await fetch(
    Routes.api_dashboard_communication_path(
      buildFilterParams({ pageParam, query, groupIds, subgroupIds, cohortIds, pinnedPlayerId })
    ),
    {
      headers: authHeaders(),
      method: 'GET',
    }
  );

  if (!response.ok) {
    throw new Error('Error fetching conversations');
  }

  return response.json();
};

const fetchConversationsUnread = async ({ pageParam, query, groupIds, subgroupIds, cohortIds }) => {
  const response = await fetch(
    Routes.api_dashboard_communication_unread_path(
      buildFilterParams({ pageParam, query, groupIds, subgroupIds, cohortIds })
    ),
    {
      headers: authHeaders(),
      method: 'GET',
    }
  );

  if (!response.ok) {
    throw new Error('Error fetching conversations');
  }

  return response.json();
};

export const useConversations = (options = {}) => {
  const {
    unreadOnly = false, query,
    groupIds, subgroupIds, cohortIds, pinnedPlayerId,
    ...restOptions
  } = options;

  return useInfiniteQuery({
    queryKey: ['conversations', { unreadOnly, query, groupIds, subgroupIds, cohortIds, pinnedPlayerId }],
    queryFn: ({ pageParam }) =>
      unreadOnly
        ? fetchConversationsUnread({ pageParam, query, groupIds, subgroupIds, cohortIds })
        : fetchConversations({ pageParam, query, groupIds, subgroupIds, cohortIds, pinnedPlayerId }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      return lastPage.page < lastPage.totalPages ? lastPage.page + 1 : undefined;
    },
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    structuralSharing: true,
    ...restOptions
  });
};

